import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import { useProfile } from 'context/profile';
import { dischargeCalendarQueryKeys, indexDischargeCalendar } from 'services/api/dischargeCalendar';
import {
  useDayViewState,
  useDischargeCalendarFilters,
  useDischargeCalendarSearch,
  useListViewSort,
  useListViewState,
  useMonthViewState,
  useWeekViewState,
} from 'stores/dischargeCalendarStore';

import { DISCHARGE_CALENDAR_VIEWS } from '../constants';

function serializeSortBy(sort: SortingState): string | undefined {
  if (!sort || sort.length === 0) return undefined;
  const { id, desc } = sort[0];
  return `${id} ${desc ? 'desc' : 'asc'}`;
}

type UseDischargeCalendarDataOptions = {
  page?: number;
  pageSize?: number;
  view: (typeof DISCHARGE_CALENDAR_VIEWS)[keyof typeof DISCHARGE_CALENDAR_VIEWS];
};

export default function useDischargeCalendarData(options: UseDischargeCalendarDataOptions) {
  const { page, pageSize, view } = options;

  const { profile } = useProfile();
  const filters = useDischargeCalendarFilters();
  const search = useDischargeCalendarSearch();

  const weekView = useWeekViewState();
  const monthView = useMonthViewState();
  const dayView = useDayViewState();
  const listView = useListViewState();
  const listViewSort = useListViewSort();

  // Get the date range based on view
  const getDateRange = () => {
    switch (view) {
      case DISCHARGE_CALENDAR_VIEWS.WEEK:
        return { startDate: weekView.startDate, endDate: weekView.endDate };
      case DISCHARGE_CALENDAR_VIEWS.MONTH:
        return { startDate: monthView.startDate, endDate: monthView.endDate };
      case DISCHARGE_CALENDAR_VIEWS.DAY:
        return { startDate: dayView.startDate, endDate: dayView.endDate };
      case DISCHARGE_CALENDAR_VIEWS.LIST:
        return { startDate: listView.startDate, endDate: listView.endDate };
      default:
        return { startDate: weekView.startDate, endDate: weekView.endDate };
    }
  };

  const { startDate, endDate } = getDateRange();

  // Only include pagination and sorting for list view
  const isListView = view === DISCHARGE_CALENDAR_VIEWS.LIST;
  const sortBy = isListView ? serializeSortBy(listViewSort) : undefined;

  const queryParams = {
    startDate,
    endDate,
    filters,
    search,
    ...(isListView && page !== undefined && { page }),
    ...(isListView && pageSize !== undefined && { pageSize }),
    ...(sortBy && { sortBy }),
  };

  return useQuery({
    queryKey: dischargeCalendarQueryKeys.index({
      clientId: profile.actingClientId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      filters,
      search,
      page,
      pageSize,
      sortBy,
    }),
    queryFn: ({ signal }) => indexDischargeCalendar(queryParams, signal),
    staleTime: 30000,
  });
}
